import React, { useState, useEffect } from 'react';
import { Card, Table, SearchInput, useIsMobile } from '../components/UI';
import '../styles/Configuration.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function Button({ onClick, children, disabled, className, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`cfg-btn${className ? ` ${className}` : ''}`}
      style={style}
    >
      {children}
    </button>
  );
}

function Alert({ type, children, onClose, style }) {
  return (
    <div className={`cfg-alert cfg-alert--${type || 'error'}`} style={style}>
      <div className="cfg-alert-inner">
        <div className="cfg-alert-body">{children}</div>
        {onClose && (
          <button className="cfg-alert-close" onClick={onClose} style={{ color: 'inherit' }}>
            ×
          </button>
        )}
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 7;

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    }
  }

  return (
    <div className="cfg-pagination">
      <Button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="cfg-btn--sm"
      >
        ← Prev
      </Button>

      {pages.map((page, idx) => (
        page === '...' ? (
          <span key={`ellipsis-${idx}`} className="cfg-pagination-ellipsis">...</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={page === currentPage ? 'cfg-page-btn cfg-page-btn--active' : 'cfg-page-btn'}
          >
            {page}
          </button>
        )
      ))}

      <Button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="cfg-btn--sm"
      >
        Next →
      </Button>
    </div>
  );
}

// =============================================================================
// HSN Configuration Tab
// =============================================================================
function HSNConfigTab() {
  const isMobile = useIsMobile();
  const [hsnCodes,     setHsnCodes]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [uploadFile,   setUploadFile]   = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [message,      setMessage]      = useState(null);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [newHSN,       setNewHSN]       = useState({ hsn_code: '', hsn_description: '', gst_rate: '', condition: '' });

  const loadHSNCodes = async (search = '', page = 1) => {
    setLoading(true);
    try {
      const url = `${API_BASE}/config/hsn?page=${page}&limit=100${search ? `&search=${encodeURIComponent(search)}` : ''}`;
      console.log('[HSN] Fetching:', url);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load HSN codes');

      const data = await res.json();
      console.log('[HSN] API Response:', data);
      console.log('[HSN] Total pages:', data.total_pages);
      console.log('[HSN] Total records:', data.total);
      console.log('[HSN] Current page:', data.page);

      setHsnCodes(data.hsn_codes || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
      setCurrentPage(data.page || 1);
    } catch (err) {
      console.error('[HSN] Error:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHSNCodes('', 1); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadHSNCodes(searchTerm, 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    console.log('[HSN] Changing to page:', page);
    setCurrentPage(page);
    loadHSNCodes(searchTerm, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/config/hsn/template/download`);
      if (!res.ok) throw new Error('Failed to download template');

      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'hsn_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: 'Template downloaded successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      setMessage({ type: 'error', text: 'Please select a file to upload' });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const res  = await fetch(`${API_BASE}/config/hsn/upload`, { method: 'POST', body: formData });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Upload failed');

      let messageText = data.message;
      if (data.failed_rows > 0) {
        messageText += `\n\nSkipped ${data.failed_rows} invalid row(s).`;
        if (data.failures && data.failures.length > 0) {
          const failureList = data.failures.slice(0, 5).map(f =>
            `Row ${f.row}: ${f.hsn_code} - ${f.reason}`
          ).join('\n');
          messageText += `\n\nFirst few failures:\n${failureList}`;
          if (data.failures.length > 5) {
            messageText += `\n... and ${data.failures.length - 5} more`;
          }
        }
      }

      setMessage({ type: 'success', text: messageText });
      setUploadFile(null);
      setCurrentPage(1);
      loadHSNCodes('', 1);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to upload file' });
    } finally {
      setUploading(false);
    }
  };

  const handleAddHSN = async () => {
    if (!newHSN.hsn_code || !newHSN.hsn_description) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    try {
      const res  = await fetch(`${API_BASE}/config/hsn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newHSN),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add HSN code');

      setMessage({ type: 'success', text: 'HSN code added successfully' });
      setNewHSN({ hsn_code: '', hsn_description: '', gst_rate: '', condition: '' });
      setShowAddForm(false);
      loadHSNCodes(searchTerm, currentPage);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const columns = [
    { key: 'hsn_code',        label: 'HSN Code',   noWrap: true, width: '15%' },
    { key: 'hsn_description', label: 'Description',              width: '20%' },
    { key: 'gst_rate',        label: 'GST Rate',
      render: (row) => row.gst_rate !== null ? `${row.gst_rate}%` : '—',
      noWrap: true, width: '15%' },
    { key: 'condition',       label: 'Condition',
      render: (row) => row.condition || '—', width: '50%' },
  ];

  return (
    <div>
      {message && (
        <Alert
          type={message.type}
          onClose={() => setMessage(null)}
          style={{ marginBottom: 20 }}
        >
          {message.text}
        </Alert>
      )}

      <div className="cfg-section-header">
        <div>
          <h2 className="cfg-section-title">HSN Codes</h2>
          <p className="cfg-section-sub">Manage HSN codes for GST validation</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancel' : '+ Add HSN Code'}
        </Button>
      </div>

      {/* Add HSN Form */}
      {showAddForm && (
        <Card className="cfg-padded-card">
          <h3 className="cfg-form-title">Add New HSN Code</h3>
          <div
            className="cfg-form-grid"
            style={{ gridTemplateColumns: isMobile ? '1fr' : '150px 1fr 120px 150px' }}
          >
            <input
              type="text"
              placeholder="HSN Code (e.g., 8471)"
              value={newHSN.hsn_code}
              onChange={(e) => setNewHSN({ ...newHSN, hsn_code: e.target.value })}
              className="cfg-input"
            />
            <input
              type="text"
              placeholder="Description (e.g., Automatic data processing machines)"
              value={newHSN.hsn_description}
              onChange={(e) => setNewHSN({ ...newHSN, hsn_description: e.target.value })}
              className="cfg-input"
            />
            <input
              type="number"
              step="0.01" min="0" max="28"
              placeholder="GST Rate (e.g., 18)"
              value={newHSN.gst_rate}
              onChange={(e) => setNewHSN({ ...newHSN, gst_rate: e.target.value })}
              className="cfg-input"
            />
            <input
              type="text"
              placeholder="Condition (e.g., branded)"
              value={newHSN.condition}
              onChange={(e) => setNewHSN({ ...newHSN, condition: e.target.value })}
              className="cfg-input"
            />
          </div>
          <Button onClick={handleAddHSN}>Add HSN Code</Button>
        </Card>
      )}

      {/* Upload Section */}
      <Card className="cfg-padded-card">
        <h3 className="cfg-form-title">Bulk Upload HSN Codes</h3>
        <p className="cfg-section-sub cfg-para-mb">
          Download the template, fill in your HSN codes, and upload it back.
          This will replace all existing HSN codes.
        </p>

        <div className="cfg-upload-row">
          <Button onClick={handleDownloadTemplate} className="cfg-btn--green">
            📥 Download Template
          </Button>

          <div className="cfg-file-wrap">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="cfg-file-input"
            />
          </div>

          <Button onClick={handleFileUpload} disabled={!uploadFile || uploading}>
            {uploading ? 'Uploading...' : '📤 Upload & Replace All'}
          </Button>
        </div>

        {uploadFile && (
          <div className="cfg-upload-warning">
            ⚠️ Warning: Uploading will delete all existing HSN codes and replace them with data from: <strong>{uploadFile.name}</strong>
          </div>
        )}
      </Card>

      {/* Search Bar */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="🔍 Search by HSN code or description..."
        />
      </Card>

      {/* Debug Info */}
      <div className="cfg-debug">
        Debug: Total Records: {totalRecords} | Current Page: {currentPage} | Total Pages: {totalPages} | HSN Codes Loaded: {hsnCodes.length}
      </div>

      {/* HSN Codes Table */}
      <Card>
        {loading ? (
          <div className="cfg-loading">Loading HSN codes...</div>
        ) : hsnCodes.length === 0 ? (
          <div className="cfg-empty">
            {searchTerm
              ? `No HSN codes found matching "${searchTerm}"`
              : 'No HSN codes configured. Download the template to get started.'
            }
          </div>
        ) : (
          <>
            <div className="cfg-table-header">
              <span>
                Showing {((currentPage - 1) * 100) + 1} - {Math.min(currentPage * 100, totalRecords)} of {totalRecords.toLocaleString()} HSN Code{totalRecords !== 1 ? 's' : ''}
              </span>
              <span className="cfg-table-header-page">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <Table
              columns={columns}
              rows={hsnCodes}
              style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// Audit Settings Tab
// =============================================================================
function AuditSettingsTab() {
  return (
    <div>
      <div className="cfg-page-header">
        <h2 className="cfg-section-title">Audit Settings</h2>
        <p className="cfg-section-sub">Configure audit thresholds and rules</p>
      </div>

      <Card style={{ padding: 24 }}>
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 40 }}>
          Audit settings configuration coming soon...
          <br />
          <small>(Overdue days threshold, high value threshold, etc.)</small>
        </p>
      </Card>
    </div>
  );
}

// =============================================================================
// Main Configuration Page
// =============================================================================
export default function Configuration() {
  const [activeTab, setActiveTab] = useState('hsn');
  const tabs = [
    { id: 'hsn',   label: 'HSN Codes',      icon: '📋' },
    { id: 'audit', label: 'Audit Settings',  icon: '⚙️' },
  ];

  return (
    <div className="cfg-page">
      <div className="cfg-page-header">
        <h1 className="cfg-page-title">Configuration</h1>
        <p className="cfg-page-sub">Manage application settings and master data</p>
      </div>

      {/* Tab Navigation */}
      <div className="cfg-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'cfg-tab cfg-tab--active' : 'cfg-tab'}
          >
            <span className="cfg-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'hsn'   && <HSNConfigTab />}
      {activeTab === 'audit' && <AuditSettingsTab />}
    </div>
  );
}
